import { NodeSelection } from "@tiptap/pm/state";
import type { Editor } from "@tiptap/react";
import { useEffect } from "react";
import type { DocumentEvidenceCitation } from "../evidence";
import {
  buildDocumentEvidenceLocatorSelector,
  buildEvidenceReferenceKey,
  collectEvidenceRuns,
  EVIDENCE_RUN_PROXY_ATTRIBUTE,
  findDocumentEvidenceElement,
  findEvidenceRunAtPos,
  isDocumentEvidenceAvailable,
  isDocumentEvidenceUnavailable,
  readDocumentEvidenceLabelFromElement,
  readDocumentEvidenceReferenceFromElement,
  readEvidenceRunCitations,
  UNAVAILABLE_DOCUMENT_EVIDENCE_TITLE,
  useDocumentEvidenceOwner,
  useOptionalDocumentEvidenceInteraction,
} from "../evidence";
import {
  getMentionActivationTitle,
  getMentionInteractionKind,
  useOptionalMentionInteraction,
} from "../mentions";
import { findMentionElement, readMentionReferenceFromElement } from "../mentions/mention-dom";

interface UseEditorMentionInteractionsOptions {
  editable: boolean;
}

/**
 * Writes an attribute only when it actually changes. The annotation pass runs from a
 * MutationObserver on the editor DOM and fires for every attribute touch inside it, so
 * unconditional writes churn attributes on every chip on every pass — and, for the
 * attributes the observer itself watches, would re-trigger the pass forever.
 */
function applyAttribute(element: HTMLElement, name: string, value: string | null | undefined) {
  if (value) {
    if (element.getAttribute(name) !== value) {
      element.setAttribute(name, value);
    }
    return;
  }
  if (element.hasAttribute(name)) {
    element.removeAttribute(name);
  }
}

function applyTitle(element: HTMLElement, title: string | null | undefined) {
  applyAttribute(element, "title", title);
}

/** The document position of the run a proxy marker stands for, or `null` for a real marker. */
function readEvidenceRunProxyPos(element: HTMLElement): number | null {
  if (!element.hasAttribute(EVIDENCE_RUN_PROXY_ATTRIBUTE)) return null;
  const parsed = Number(element.getAttribute("data-evidence-run-pos"));
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
}

export function useEditorMentionInteractions(
  editor: Editor | null,
  options: UseEditorMentionInteractionsOptions
) {
  const mentionInteraction = useOptionalMentionInteraction();
  const evidenceInteraction = useOptionalDocumentEvidenceInteraction();
  const evidenceOwner = useDocumentEvidenceOwner();

  useEffect(() => {
    if (!editor) return;
    let dom: HTMLElement | null = null;
    let mutationObserver: MutationObserver | null = null;
    let animationFrameId: number | null = null;
    let isCancelled = false;

    const updateMentionAnnotations = () => {
      if (!dom) return;
      const mentionElements = dom.querySelectorAll<HTMLElement>("[data-mention-type]");
      for (const element of mentionElements) {
        const reference = readMentionReferenceFromElement(element);
        const interactionKind =
          reference && mentionInteraction
            ? mentionInteraction.getInteractionKind(reference.mentionType)
            : reference
              ? getMentionInteractionKind(reference.mentionType)
              : "unsupported";
        const isInteractive = Boolean(mentionInteraction) && interactionKind === "navigate";
        const title = getMentionActivationTitle({
          interactionKind,
          editable: options.editable,
          enabled: isInteractive,
        });

        element.dataset.mentionInteraction = interactionKind;
        element.classList.toggle("mention--interactive", isInteractive);
        element.classList.toggle("reference-chip--interactive", isInteractive);

        applyTitle(element, title);

        if (isInteractive && !options.editable) {
          element.tabIndex = 0;
          element.setAttribute("role", "link");
        } else {
          element.removeAttribute("tabindex");
          element.removeAttribute("role");
        }
      }
    };

    /**
     * A proxy marker is plugin-built DOM, so React never stamps the resolved presentation
     * on it. Copy it from the real marker it stands for — same locator, same answer — before
     * the pass below decides whether the marker is interactive.
     */
    const syncEvidenceProxyAnnotations = () => {
      if (!dom) return;
      const proxies = dom.querySelectorAll<HTMLElement>(`[${EVIDENCE_RUN_PROXY_ATTRIBUTE}]`);
      for (const proxy of proxies) {
        const selector = buildDocumentEvidenceLocatorSelector(proxy, { proxy: false });
        const source = selector ? dom.querySelector<HTMLElement>(selector) : null;
        if (!source) continue;
        applyAttribute(
          proxy,
          "data-evidence-available",
          source.getAttribute("data-evidence-available")
        );
        // The live document name, which only the authorized presentation knows.
        const label = source.getAttribute("data-evidence-label");
        applyAttribute(proxy, "data-evidence-label", label);
        applyAttribute(proxy, "aria-label", label);
      }
    };

    const updateEvidenceAnnotations = () => {
      if (!dom) return;
      syncEvidenceProxyAnnotations();
      const evidenceElements = dom.querySelectorAll<HTMLElement>('[data-type="documentEvidence"]');
      for (const element of evidenceElements) {
        const reference = readDocumentEvidenceReferenceFromElement(element);
        const isInteractive = Boolean(
          evidenceInteraction && reference && isDocumentEvidenceAvailable(element)
        );

        element.classList.toggle("reference-chip--interactive", isInteractive);

        // A collapsed run shows one marker for several citations; say how many.
        const runSize = Number(element.getAttribute("data-evidence-run-size") ?? "1");
        const activationTitle =
          Number.isSafeInteger(runSize) && runSize > 1
            ? `View document evidence (${runSize})`
            : "View document evidence";

        applyTitle(
          element,
          isInteractive
            ? activationTitle
            : isDocumentEvidenceUnavailable(element)
              ? UNAVAILABLE_DOCUMENT_EVIDENCE_TITLE
              : null
        );

        // `aria-haspopup` only makes sense alongside the button role and focusability; in
        // editable mode the atom is reached through the editor selection, not the tab order.
        if (isInteractive && !options.editable) {
          element.tabIndex = 0;
          element.setAttribute("role", "button");
          element.setAttribute("aria-haspopup", "dialog");
        } else {
          element.removeAttribute("tabindex");
          element.removeAttribute("role");
          element.removeAttribute("aria-haspopup");
        }
      }
    };

    const updateReferenceAnnotations = () => {
      updateMentionAnnotations();
      updateEvidenceAnnotations();
    };

    const activateMention = async (target: EventTarget | null) => {
      const mentionElement = findMentionElement(target);
      const reference = readMentionReferenceFromElement(mentionElement);
      if (!reference || !mentionInteraction) return false;
      if (mentionInteraction.getInteractionKind(reference.mentionType) !== "navigate") {
        return false;
      }

      return await mentionInteraction.activateMention(reference);
    };

    const shouldHandleActivationTarget = (target: EventTarget | null) => {
      const mentionElement = findMentionElement(target);
      const reference = readMentionReferenceFromElement(mentionElement);
      if (!reference || !mentionInteraction) return false;
      return mentionInteraction.getInteractionKind(reference.mentionType) === "navigate";
    };

    /**
     * The clicked marker may stand for a whole run of adjacent citations, so the citation
     * list is read back from the document rather than from the DOM: the continuations are
     * hidden and the adjacency rule lives in one place (`evidence-runs`).
     */
    const collectRunCitations = (
      element: HTMLElement,
      reference: ReturnType<typeof readDocumentEvidenceReferenceFromElement>
    ): DocumentEvidenceCitation[] => {
      const clicked = reference
        ? [{ reference, fallbackLabel: readDocumentEvidenceLabelFromElement(element) }]
        : [];
      try {
        // A proxy stands where its run is *drawn*, not where it lives, so `posAtDOM` would
        // resolve the host line. The run's own position travels on the element instead.
        const proxyPos = readEvidenceRunProxyPos(element);
        const pos = proxyPos ?? editor.view.posAtDOM(element, 0);
        const run = pos < 0 ? null : findEvidenceRunAtPos(editor.state.doc, pos);
        const citations = run ? readEvidenceRunCitations(run) : [];
        const [head] = citations;
        if (!head) return clicked;
        // The widget's DOM is reused across decoration rebuilds, so the position it carries
        // can lag an edit. A run that no longer starts with the cited passage means exactly
        // that: the run moved. Find it again by the passage the marker names — a stale
        // position must not shrink a multi-citation run to a single row.
        if (
          proxyPos !== null &&
          reference &&
          buildEvidenceReferenceKey(head.reference) !== buildEvidenceReferenceKey(reference)
        ) {
          const markerKey = buildEvidenceReferenceKey(reference);
          const moved = collectEvidenceRuns(editor.state.doc)
            .map(readEvidenceRunCitations)
            .find((candidates) => {
              const first = candidates[0];
              return first ? buildEvidenceReferenceKey(first.reference) === markerKey : false;
            });
          return moved && moved.length > 0 ? moved : clicked;
        }
        return citations;
      } catch {
        // A marker whose position cannot be resolved (a detached or diff-rendered view)
        // still opens for itself rather than not opening at all.
        return clicked;
      }
    };

    /**
     * Keyboard node-selection can land on a hidden continuation marker of a collapsed
     * run; anchoring the popover there would position it against a `display: none`
     * element. Walk back to the run's visible head marker instead.
     */
    const resolveRunHeadAnchor = (element: HTMLElement): HTMLElement => {
      if (element.getAttribute("data-evidence-run-role") !== "continuation") return element;
      let sibling = element.parentElement?.previousElementSibling ?? null;
      while (sibling) {
        const chip = sibling.matches('[data-type="documentEvidence"]')
          ? sibling
          : sibling.querySelector('[data-type="documentEvidence"]');
        if (!(chip instanceof HTMLElement)) break;
        const role = chip.getAttribute("data-evidence-run-role");
        if (role === "head") return chip;
        if (role !== "continuation") break;
        sibling = sibling.previousElementSibling;
      }
      return element;
    };

    const resolvePopoverAnchor = (element: HTMLElement): HTMLElement => {
      const anchor = resolveRunHeadAnchor(element);
      if (anchor.hasAttribute(EVIDENCE_RUN_PROXY_ATTRIBUTE)) return anchor;
      // Every marker of a hoisted run is hidden where it is stored, and keyboard selection
      // can still land on one. Anchor the popover to the proxy drawn for that run instead.
      if (anchor.offsetParent !== null) return anchor;
      const selector = buildDocumentEvidenceLocatorSelector(anchor, { proxy: true });
      const proxy = selector ? (dom?.querySelector<HTMLElement>(selector) ?? null) : null;
      return proxy ?? anchor;
    };

    const activateEvidence = (target: EventTarget | null) => {
      const evidenceElement = findDocumentEvidenceElement(target);
      const reference = readDocumentEvidenceReferenceFromElement(evidenceElement);
      if (
        !reference ||
        !evidenceElement ||
        !evidenceInteraction ||
        !isDocumentEvidenceAvailable(evidenceElement)
      ) {
        return false;
      }

      evidenceInteraction.activateEvidenceGroup(
        collectRunCitations(evidenceElement, reference),
        resolvePopoverAnchor(evidenceElement),
        { owner: evidenceOwner }
      );
      return true;
    };

    const shouldHandleEvidenceActivationTarget = (target: EventTarget | null) => {
      const evidenceElement = findDocumentEvidenceElement(target);
      return Boolean(
        evidenceInteraction &&
          isDocumentEvidenceAvailable(evidenceElement) &&
          readDocumentEvidenceReferenceFromElement(evidenceElement)
      );
    };

    const handleClick = (event: MouseEvent) => {
      const isMention = shouldHandleActivationTarget(event.target);
      const isEvidence = shouldHandleEvidenceActivationTarget(event.target);
      if (!isMention && !isEvidence) return;
      event.preventDefault();
      event.stopPropagation();
      if (isEvidence) {
        activateEvidence(event.target);
      } else {
        void activateMention(event.target);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      // A modified Enter is the editor's (newline, submit) and an IME's Enter commits its
      // composition; this listener runs first, so it must leave those alone.
      if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey || event.isComposing) {
        return;
      }

      let activationTarget = event.target;
      if (options.editable && editor.state.selection instanceof NodeSelection) {
        const selectedDom = editor.view.nodeDOM(editor.state.selection.from);
        activationTarget =
          selectedDom instanceof HTMLElement
            ? ((selectedDom.matches('[data-type="documentEvidence"], [data-mention-type]')
                ? selectedDom
                : selectedDom.querySelector<HTMLElement>(
                    '[data-type="documentEvidence"], [data-mention-type]'
                  )) ?? selectedDom)
            : selectedDom;
      } else if (options.editable) {
        return;
      }

      const isMention = shouldHandleActivationTarget(activationTarget);
      const isEvidence = shouldHandleEvidenceActivationTarget(activationTarget);
      if (!isMention && !isEvidence) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (isEvidence) {
        activateEvidence(activationTarget);
      } else {
        void activateMention(activationTarget);
      }
    };

    const attachWhenViewReady = (remainingAttempts = 10) => {
      if (isCancelled || editor.isDestroyed) {
        return;
      }

      try {
        dom = editor.view.dom instanceof HTMLElement ? editor.view.dom : null;
      } catch {
        dom = null;
      }

      if (!(dom instanceof HTMLElement)) {
        if (remainingAttempts <= 0) {
          return;
        }
        animationFrameId = window.requestAnimationFrame(() => {
          attachWhenViewReady(remainingAttempts - 1);
        });
        return;
      }

      updateReferenceAnnotations();

      mutationObserver = new MutationObserver(() => {
        updateReferenceAnnotations();
      });
      mutationObserver.observe(dom, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          "class",
          "data-id",
          "data-mention-id",
          "data-mention-type",
          "data-artifact-id",
          "data-processing-revision-id",
          "data-block-id",
          "data-start-char",
          "data-end-char",
          "data-evidence-label",
          "data-evidence-available",
          "data-evidence-run-size",
          "data-evidence-run-pos",
        ],
      });

      dom.addEventListener("click", handleClick);
      // Capture, so activation runs before ProseMirror's own keydown listener on this
      // element: its keymap would otherwise act on the selected chip first (Enter splits the
      // paragraph) and the keystroke would never open the chip.
      dom.addEventListener("keydown", handleKeyDown, true);
    };

    attachWhenViewReady();

    return () => {
      isCancelled = true;
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      mutationObserver?.disconnect();
      dom?.removeEventListener("click", handleClick);
      dom?.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [editor, evidenceInteraction, evidenceOwner, mentionInteraction, options.editable]);
}
